import { ArrowRightIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { createPortal } from 'react-dom'

import { EmployeeEntry } from 'components/quoting/quotes/employee_entry/EmployeeEntry'
import { useEmployer } from 'pages/employer_onboarding/EmployerProvider'
import { usePrefill } from 'pages/employer_onboarding/PrefillProvider'
import { Button } from 'slab'
import { useGetQuotingProspectsProspectIdCensusCensusIdEmployees } from 'utils/web_api'
import { type QuotingEmployeeCensus } from 'utils/web_api.schemas'

interface ManualEntryProps {
  buttonPortalContainer: HTMLElement | null;
  census?: QuotingEmployeeCensus;
  onSuccess: (censusId: string) => void;
}

// TODO: Make this paginate through all the employees in the census
export function ManualEntry ({
  buttonPortalContainer,
  census,
  onSuccess,
}: ManualEntryProps) {
  const { employer } = useEmployer()
  const { getPrefillValue } = usePrefill()
  const prospectId = employer?.quoting_prospect_id ?? ''

  const {
    data: employees,
    isLoading,
    isError,
  } = useGetQuotingProspectsProspectIdCensusCensusIdEmployees(
    prospectId,
    census?.id ?? '',
    {
      'page[size]': 100,
    },
    {
      query: {
        refetchOnMount: 'always',
        enabled: !!census,
      },
    }
  )

  // Use demo employees from prefill if no existing employees and prefill is enabled
  const demoEmployees = getPrefillValue('census_employees')
  const existingEmployees = employees?.data || (demoEmployees && !census ? demoEmployees : undefined)

  function renderNextButton ({
    onSubmit,
    isSubmitDisabled,
  }: {
    onSubmit: () => Promise<void>;
    isSubmitDisabled: boolean;
  }) {
    if (buttonPortalContainer) {
      return createPortal(
        <Button
          disabled={isSubmitDisabled}
          onClick={onSubmit}
          rightIcon={ArrowRightIcon}
          size='large'
          variant='primary'
        >
          Next step
        </Button>,
        buttonPortalContainer
      )
    }

    return null
  }

  function renderContent () {
    if (census) {
      if (isLoading) {
        return <div>Loading...</div>
      }

      if (isError) {
        return <div>Error</div>
      }
    }

    return (
      <EmployeeEntry
        existingCensusId={census?.id}
        existingEmployees={existingEmployees}
        onEmployeesAdded={async (censusId) => {
          onSuccess(censusId)
        }}
        prospectId={prospectId}
        renderNavButtons={renderNextButton}
      />
    )
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      {renderContent()}
    </motion.div>
  )
}
